import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const fmt = (n) => Number(n).toLocaleString("de-DE", { maximumFractionDigits: 4 });
const keyOf = (r) => r.item_id + "/" + (r.batch_id || "ohne");
const valid = (s) => /^\d+(?:[,.]\d{1,4})?$/.test(s) && Number.isFinite(Number(s.replace(",", ".")));

function CountRow({ row, update, reload }) {
  const [entry, setEntry] = useState("");
  const [request, setRequest] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [conflict, setConflict] = useState(false);
  const isValid = valid(entry);
  const quantity = isValid ? Number(entry.replace(",", ".")) : null;
  const submit = async (e) => {
    e.preventDefault();
    if (!isValid || busy || conflict) return;
    setBusy(true);
    setMessage("");
    const id = request || crypto.randomUUID();
    setRequest(id);
    try {
      const { data } = await api.post("/stocktake", { item_id: row.item_id, batch_id: row.batch_id,
        quantity, revision: row.revision, request_id: id });
      update(data);
      setEntry("");
      setRequest(null);
      setMessage("Gespeichert.");
    } catch (err) {
      setConflict(err.response?.status === 409);
      const detail = err.response?.data?.detail;
      setMessage(typeof detail === "string" ? detail : "Buchung nicht bestätigt. Erneut versuchen.");
    } finally { setBusy(false); }
  };
  return <li className="border-b p-4">
    <form onSubmit={submit} className="flex flex-wrap items-center justify-between gap-3">
      <div className="min-w-48">
        <strong>{row.name}</strong> <span className="text-xs text-[var(--text-muted)]">{row.sku}</span>
        <div>{row.charge || (row.batch_id ? "Charge ohne Nummer" : "Ohne Chargenzuordnung")} · MHD {row.mhd?.slice(0, 10) || "—"}</div>
        <div className="text-xs text-[var(--text-muted)]">{row.location || "Ohne Lagerplatz"} · Buchbestand {fmt(row.stock)} {row.unit}
          {row.counted_at && " · Zuletzt gezählt " + new Date(row.counted_at).toLocaleString("de-DE")}
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs">Gezählte Menge ({row.unit})
          <Input inputMode="decimal" autoComplete="off" value={entry} disabled={busy}
            placeholder="Menge" className="mt-1 w-32"
            onChange={e => { setEntry(e.target.value.trim()); setRequest(null); setMessage(""); }} />
        </label>
        <Button type="submit" disabled={busy || !isValid || conflict}>{busy ? "Speichert …" : "Übernehmen"}</Button>
      </div>
    </form>
    {isValid && <p className="mt-1 text-xs">Neuer Bestand {fmt(quantity)} {row.unit} · Differenz {fmt(quantity - row.stock)} {row.unit}</p>}
    {entry && !isValid && <p className="mt-1 text-xs">Menge ab 0 mit höchstens vier Nachkommastellen eingeben.</p>}
    <div role="status" className="mt-1 text-sm">{message}
      {conflict && <button className="ml-2 underline" onClick={async () => {
        try { await reload(); setConflict(false); setRequest(null); setMessage("Aktualisiert. Menge erneut prüfen."); }
        catch { setMessage("Aktualisierung fehlgeschlagen."); }
      }}>Zeile aktualisieren</button>}
    </div>
  </li>;
}

export default function Stocktake() {
  const [rows, setRows] = useState(null);
  const [unresolved, setUnresolved] = useState(0);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("");
  const [openOnly, setOpenOnly] = useState(false);
  const [limit, setLimit] = useState(60);
  const load = async () => {
    const { data } = await api.get("/stocktake");
    setRows(data.positions);
    setUnresolved(data.unresolved);
    setError("");
    return data.positions;
  };
  useEffect(() => { load().catch(e => setError(e.response?.status === 403 ? "Für die Inventur ist eine Lagerberechtigung erforderlich." : "Inventurliste konnte nicht geladen werden.")); }, []);
  const update = (row) => setRows(list => list.map(old => keyOf(old) === keyOf(row) ? row : old));
  const counted = (rows || []).filter(r => r.counted_at).length;
  const found = (rows || []).filter(r => (!openOnly || !r.counted_at) &&
    [r.name, r.sku, r.charge, r.location, r.mhd].some(s => s?.toLowerCase().includes(filter.trim().toLowerCase())));
  return <div>
    <Link to="/lager" className="text-sm underline">Zum Lagerbestand</Link>
    <div className="mt-4"><PageHeader eyebrow="Lager" title="Inventur" description="Je Charge die tatsächlich gezählte Gesamtmenge in der Artikeleinheit eingeben. Eine 0 bestätigt eine leere Position. Leere Felder bleiben offen." /></div>
    {error && <p role="alert">{error}</p>}
    {rows === null && !error && <p>Lade Inventurliste …</p>}
    {rows && <>
      <div className="mb-4 flex flex-wrap items-center gap-4">
        <Input aria-label="Inventur durchsuchen" placeholder="Artikel, Charge oder Lagerplatz suchen" className="max-w-md"
          value={filter} onChange={e => { setFilter(e.target.value); setLimit(60); }} />
        <label><input type="checkbox" checked={openOnly} onChange={e => { setOpenOnly(e.target.checked); setLimit(60); }} /> Nur ungezählte Positionen</label>
        <span className="text-sm">{counted} von {rows.length} Positionen gezählt</span>
      </div>
      {unresolved > 0 && <p role="alert" className="mb-3 rounded bg-amber-100 p-3">{unresolved} Bestandsposition(en) mit fehlendem Artikel- oder Chargenverweis müssen separat geklärt werden.</p>}
      <ul className="rounded-xl border bg-[var(--surface)]">
        {found.slice(0, limit).map(row => <CountRow row={row} key={keyOf(row)} update={update} reload={async () => {
          const { data } = await api.get("/stocktake");
          const fresh = data.positions.find(r => keyOf(r) === keyOf(row));
          if (!fresh) throw Error("Position fehlt");
          update(fresh);
        }} />)}
        {found.length === 0 && <li className="p-5">Keine passenden Inventurpositionen.</li>}
      </ul>
      {limit < found.length && <Button className="mt-3" onClick={() => setLimit(n => n + 60)}>Weitere Positionen anzeigen</Button>}
    </>}
  </div>;
}
